import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RecentWithdrawalsComponent, WithdrawalItem } from '../../../types/builder';
import { CheckCircle2 } from 'lucide-react';
import { BankLogo, type BankId } from './BankLogos';

interface RecentWithdrawalsRendererProps {
  component: RecentWithdrawalsComponent;
}

const THAI_MONTHS_SHORT = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
];

function formatThaiDate(date: Date): string {
  const d = date.getDate();
  const m = THAI_MONTHS_SHORT[date.getMonth()];
  const y = date.getFullYear() + 543;
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${d} ${m} ${y} ${hh}:${mm}:${ss}`;
}

export const RecentWithdrawalsRenderer: React.FC<RecentWithdrawalsRendererProps> = ({ component }) => {
  const { settings } = component;
  const [displayItems, setDisplayItems] = useState<WithdrawalItem[]>(settings.items);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setDisplayItems(settings.items);
  }, [settings.items]);

  useEffect(() => {
    if (!settings.autoScroll || settings.items.length === 0) return;

    const interval = setInterval(() => {
      setDisplayItems((prevItems) => {
        if (prevItems.length === 0) return prevItems;
        const [first, ...rest] = prevItems;
        return [...rest, first];
      });
      setTick((t) => t + 1);
    }, settings.scrollSpeed || 3000);

    return () => clearInterval(interval);
  }, [settings.autoScroll, settings.scrollSpeed, settings.items.length]);

  // Auto generate Thai dates that count down from "now" so the freshest item is on top
  const generatedDates = useMemo(() => {
    const out: string[] = [];
    const now = new Date();
    for (let i = 0; i < displayItems.length; i++) {
      const d = new Date(now.getTime() - i * 3 * 1000);
      out.push(formatThaiDate(d));
    }
    return out;
    // re-roll on each rotation tick so the dates stay "live"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayItems.length, tick]);

  return (
    <BankStyleList
      component={component}
      displayItems={displayItems}
      generatedDates={generatedDates}
    />
  );
};

interface BankStyleProps {
  component: RecentWithdrawalsComponent;
  displayItems: WithdrawalItem[];
  generatedDates: string[];
}

const BankStyleList: React.FC<BankStyleProps> = ({ component, displayItems, generatedDates }) => {
  const { t } = useTranslation('builder');
  const { settings } = component;
  const accent = settings.accentColor || '#FFD43B';
  const textColor = settings.textColor || '#FFFFFF';
  const pendingLabel = settings.pendingLabel || 'กำลังโอน...';
  const successLabel = settings.successLabel || 'โอนแล้ว';
  const amountSuffix = settings.amountSuffix || 'บาท';
  const useAutoDates = settings.autoGenerateDates !== false;

  const items = displayItems.slice(0, settings.maxItems);

  return (
    <div
      style={{
        backgroundColor: settings.backgroundColor,
        padding: `${settings.padding}px`,
        color: textColor,
      }}
    >
      {settings.title && (
        <h3
          className="text-2xl md:text-3xl font-extrabold text-center mb-5"
          style={{ color: textColor, textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
        >
          {settings.title}
        </h3>
      )}

      <div className="space-y-3 overflow-hidden">
        {items.map((item, index) => {
          // Top item is always the "pending" one (transferring) for the animated feel
          const status: 'pending' | 'success' =
            item.status || (index === 0 && settings.autoScroll ? 'pending' : 'success');
          const bankId = (item.bank as BankId) || 'kbank';
          const dateText = item.date || (useAutoDates ? generatedDates[index] : item.time) || item.time;

          return (
            <div
              key={`${item.id}-${index}`}
              className="rwBankCard relative flex items-center gap-3 px-4 py-3 rounded-full"
              style={{
                backgroundColor: settings.itemBackgroundColor || 'rgba(20, 80, 50, 0.45)',
                border: '1px solid rgba(255,255,255,0.12)',
                boxShadow:
                  '0 4px 18px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                animation: settings.autoScroll ? 'rwBankSlide 0.55s ease-out' : 'none',
              }}
            >
              <BankLogo bank={bankId} size={48} />

              <div className="flex-1 min-w-0">
                <div className="flex items-baseline flex-wrap gap-x-2 gap-y-0.5 leading-tight">
                  <span className="text-sm md:text-base font-semibold opacity-90">ยูสเซอร์:</span>
                  <span className="text-sm md:text-base font-extrabold tracking-wide">
                    {item.username}
                  </span>
                </div>
                <div className="flex items-baseline flex-wrap gap-x-2 gap-y-0.5 leading-tight mt-0.5">
                  <span className="text-sm md:text-base font-semibold opacity-90">ยอดถอน:</span>
                  <span
                    className="text-base md:text-lg font-extrabold tracking-wide"
                    style={{ color: accent, textShadow: '0 0 10px rgba(255,212,59,0.35)' }}
                  >
                    {item.amount}
                  </span>
                  <span className="text-sm md:text-base font-semibold" style={{ color: accent }}>
                    {amountSuffix}
                  </span>
                </div>
                <div className="text-[11px] md:text-xs opacity-60 mt-0.5">{dateText}</div>
              </div>

              <div className="flex-shrink-0">
                {status === 'pending' ? (
                  <div
                    className="flex items-center gap-1.5 pl-3 pr-3 py-1.5 rounded-full"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.06)',
                      color: '#E5E7EB',
                    }}
                  >
                    <span
                      className="rwSpinner inline-block w-3 h-3 rounded-full"
                      style={{
                        border: '2px dashed rgba(255,255,255,0.7)',
                        borderTopColor: 'transparent',
                      }}
                    />
                    <span className="text-xs md:text-sm font-medium whitespace-nowrap">
                      {pendingLabel}
                    </span>
                  </div>
                ) : (
                  <div
                    className="flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full"
                    style={{
                      backgroundColor: 'rgba(16, 185, 129, 0.18)',
                      border: '1px solid rgba(16,185,129,0.45)',
                      color: '#A7F3D0',
                    }}
                  >
                    <CheckCircle2 size={14} className="text-emerald-300" />
                    <span className="text-xs md:text-sm font-semibold whitespace-nowrap">
                      {successLabel}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {items.length === 0 && (
          <div
            className="text-center py-8 opacity-60 text-sm"
            style={{ color: textColor }}
          >
            {t('settings.recentWithdrawals.emptyState')}
          </div>
        )}
      </div>

      <style>{`
        @keyframes rwBankSlide {
          0% { transform: translateY(-12px) scale(0.98); opacity: 0; }
          60% { opacity: 1; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes rwSpin {
          to { transform: rotate(360deg); }
        }
        .rwSpinner { animation: rwSpin 0.9s linear infinite; }
        .rwBankCard:hover {
          transform: translateY(-1px);
          transition: transform 0.2s ease;
        }
      `}</style>
    </div>
  );
};

// Re-export bank info for the settings panel
export { THAI_BANKS } from './BankLogos';
